import { getCoordinatesFromGeohashItem } from '../geohash.js';
import { GeoStyleToIcon } from '../../../modules/GeoStyleToIcon.js';

/** @import { Geohash, GeohashKey, GeohashItem } from '../geohash.js' */
/** @import { JobEntityReference } from '../job-entity.js' */

/**
 * @typedef {Object} RenderableJobFeature
 * @property {JobEntityReference} mapItem
 * @property {object} geoJson A GeoJSON Feature describing the entity's geometry and render style.
 * @property {boolean} [draggable] Whether the pointer may drag this feature on the map. Only nodes are draggable.
 */

/**
 * Converts a job's geohash data into generic, engine-agnostic renderable features.
 * @param {string} jobId
 * @param {Geohash} geohash
 * @returns {RenderableJobFeature[]}
 * @throws {Error} If a geohash key does not match the node, connection, or section key patterns.
 */
export function getRenderableFeaturesFromGeohash(jobId, geohash) {
  const features = [];

  for (const geohashKey in geohash) {
    const geohashItem = geohash[geohashKey];
    if (geohashKeyIsNode(geohashKey)) {
      features.push(getNodeFeature(jobId, geohashKey, geohashItem));
    } else if (geohashKeyIsConnection(geohashKey)) {
      features.push(getConnectionFeature(jobId, geohashKey, geohashItem));
    } else if (geohashKeyIsSection(geohashKey)) {
      features.push(getSectionFeature(jobId, geohashKey, geohashItem));
    } else {
      throw new Error(`Unknown geohash key pattern: ${geohashKey}`);
    }
  }

  return features;
}

/**
 * Computes the geographic bounds spanning every coordinate in a geohash.
 * @param {Geohash} geohash
 * @returns {?{west: number, south: number, east: number, north: number}} Degrees, or null if the geohash is empty.
 */
export function getBoundsFromGeohash(geohash) {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;

  for (const geohashKey in geohash) {
    const coordinates = getCoordinatesFromGeohashItem(geohash[geohashKey]);
    for (const [lat, lng] of coordinates) {
      west = Math.min(west, lng);
      east = Math.max(east, lng);
      south = Math.min(south, lat);
      north = Math.max(north, lat);
    }
  }
  // Arbitrarily check west to see if the directions haven't been updated (no geohash) return null
  if (west === Infinity) return null;

  return { west, south, east, north };
}

/**
 * Builds the renderable feature for a node geohash item.
 * @param {string} jobId
 * @param {GeohashKey} geohashKey
 * @param {GeohashItem} geohashItem
 * @returns {RenderableJobFeature}
 */
function getNodeFeature(jobId, geohashKey, geohashItem) {
  const jobEntity = { jobId, type: 'node', nodeId: geohashKey };
  const icon = GeoStyleToIcon(geohashItem) ?? undefined;
  const coordinates = getCoordinatesFromGeohashItem(geohashItem)[0].toReversed();
  const geoJson = {
    type: 'Feature',
    geometry: { type: 'Point', coordinates },
    properties: { icon, order: geohashItem.z ?? 0 }
  };
  // Nodes are the only job entities a user can reposition by dragging.
  return { mapItem: jobEntity, geoJson, draggable: true };
}

/**
 * Builds the renderable feature for a connection geohash item.
 * @param {string} jobId
 * @param {GeohashKey} geohashKey
 * @param {GeohashItem} geohashItem
 * @returns {RenderableJobFeature}
 */
function getConnectionFeature(jobId, geohashKey, geohashItem) {
  const [connectionId, endpointId] = geohashKey.split('~');
  const jobEntity = { jobId, type: 'connection', connectionId };

  const coordinates = getCoordinatesFromGeohashItem(geohashItem).map((coord) => coord.toReversed());
  // Swap the initial and final coordinates for endpoint 2 since they are stored in reverse order.
  if (endpointId == '2') {
    const initialCoordinate = coordinates[0];
    coordinates[0] = coordinates[coordinates.length - 1];
    coordinates[coordinates.length - 1] = initialCoordinate;
  }

  const geoJson = {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates },
    properties: {
      color: geohashItem.c || '#FFFFFF',
      width: geohashItem.w || 6,
      opacity: geohashItem.o ?? 0.7,
      dashed: geohashItem.p === 'dashed'
    }
  };
  return { mapItem: jobEntity, geoJson };
}

/**
 * Builds the renderable feature for a section geohash item.
 * @param {string} jobId
 * @param {GeohashKey} geohashKey
 * @param {GeohashItem} geohashItem
 * @returns {RenderableJobFeature}
 */
function getSectionFeature(jobId, geohashKey, geohashItem) {
  const [connectionId, sectionId] = geohashKey.split(':');
  const jobEntity = { jobId, type: 'section', connectionId, sectionId };

  const coordinates = getCoordinatesFromGeohashItem(geohashItem)[0].toReversed();
  const icon = GeoStyleToIcon(geohashItem) ?? undefined;

  const geoJson = {
    type: 'Feature',
    geometry: { type: 'Point', coordinates },
    properties: { icon, order: geohashItem.z ?? 0 }
  };
  return { mapItem: jobEntity, geoJson };
}

/** @param {GeohashKey} geohashKey @returns {boolean} */
function geohashKeyIsNode(geohashKey) {
  return !geohashKey.includes('~') && !geohashKey.includes(':');
}

/** @param {GeohashKey} geohashKey @returns {boolean} */
function geohashKeyIsConnection(geohashKey) {
  return geohashKey.includes('~');
}

/** @param {GeohashKey} geohashKey @returns {boolean} */
function geohashKeyIsSection(geohashKey) {
  return geohashKey.includes(':');
}
